import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useServices } from '../app/services';
import { usePremium } from '../premium/PremiumProvider';

/** The screens that carry the banner: browsing, not doing (never Quick Add, a form, a bill or settings). */
const AD_SCREENS = new Set(['/', '/analytics']);

/** Whether a sheet or dialog is open: the native banner would sit over its buttons. */
function useDialogOpen(): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const check = () => setOpen(Boolean(document.querySelector('[role="dialog"], [aria-modal="true"]')));
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['role', 'aria-modal'] });
    return () => observer.disconnect();
  }, []);
  return open;
}

/** Where the tab bar starts, from the bottom: the banner sits right above it (0 on the wide layout's side rail). */
function tabBarOffset(): number {
  const bar = document.querySelector('.tabbar');
  if (!bar) return 0;
  const r = bar.getBoundingClientRect();
  return r.width >= window.innerWidth - 1 ? Math.max(0, Math.round(window.innerHeight - r.top)) : 0;
}

/**
 * The free app's one banner (Premium phase 5): on Home and Analytics, above
 * the tab bar, hidden whenever a sheet is open, and never with Premium (nor
 * before the store has said, so a Premium user never sees one flash). The
 * page leaves room for it (--ad-h), so it covers nothing.
 */
export function AdBanner() {
  const { ads } = useServices();
  const premium = usePremium();
  const { pathname } = useLocation();
  const dialogOpen = useDialogOpen();
  const wanted = Boolean(ads) && premium.ready && !premium.has('noAds') && AD_SCREENS.has(pathname) && !dialogOpen;

  useEffect(() => {
    if (!ads) return;
    return ads.onHeight((h) => document.documentElement.style.setProperty('--ad-h', `${Math.round(h)}px`));
  }, [ads]);

  useEffect(() => {
    if (!ads) return;
    if (wanted) void ads.show(tabBarOffset());
    else {
      void ads.hide();
      document.documentElement.style.setProperty('--ad-h', '0px');
    }
  }, [ads, wanted]);

  return null;
}
